import {
  PTable,
  PTableHead,
  PTableHeadCell,
  PTableHeadRow,
  PTableBody,
  PTableRow,
  PTableCell,
  PText,
  PSpinner
} from '@porsche-design-system/components-react'
import type { SortingChangeEvent } from '@porsche-design-system/components-react'
import clsx from 'clsx'
import React from 'react'

import styles from './tablewithaction.module.css'

type HeadSorting = {
  id: string
  name: string
  active?: boolean
  direction?: 'asc' | 'desc'
}

type DataSorting = {
  additionalInformation: any
  col0: string
  col1: string
  col2?: string
  col3?: string
  col4?: string
}

type Props = {
  headlines: string[]
  caption: string
  content: DataSorting[]
  loading: boolean
  rowClicker: (item: any) => void
}

const TableWithAction: React.FC<Props> = ({ headlines, caption, content, loading, rowClicker }) => {
  const headSorting: HeadSorting[] = headlines.map<HeadSorting>((item, i) => ({
    id: 'col' + i,
    name: item,
    active: i === 0,
    direction: 'asc'
  }))

  const [head, setHead] = React.useState(headSorting)
  const [data, setData] = React.useState(content)

  const onSortingChange = React.useCallback((e: CustomEvent<SortingChangeEvent>) => {
    const { id, direction } = e.detail as SortingChangeEvent & { id: keyof DataSorting }
    setHead((prev) => prev.map((item) => ({ ...item, active: false, ...(item.id === id && e.detail) })))
    setData((prev) =>
      [...prev].sort((a, b) => {
        return direction === 'asc'
          ? a[id]!.localeCompare(b[id]!, 'en', { numeric: true })
          : b[id]!.localeCompare(a[id]!, 'en', { numeric: true })
      })
    )
  }, [])

  React.useEffect(() => {
    setData(content)
  }, [content])

  return (
    <>
      <PTable caption={caption} onSortingChange={onSortingChange}>
        <PTableHead>
          <PTableHeadRow>
            {head.map((headline, i) => (
              <PTableHeadCell key={i} sort={headline}>
                {headline.name}
              </PTableHeadCell>
            ))}
          </PTableHeadRow>
        </PTableHead>
        <PTableBody>
          {!loading &&
            data.length > 0 &&
            data.map((item, i) => (
              <PTableRow key={i} onClick={() => rowClicker(item)} className={styles.clickable}>
                <PTableCell>
                  <div
                    className={clsx(
                      item.additionalInformation.appType === 'subApp' && styles.subApp,
                      item.additionalInformation.appType === 'module' && styles.module
                    )}
                  >
                    {item.col0}
                  </div>
                </PTableCell>
                <PTableCell>{item.col1}</PTableCell>
                <PTableCell>{item.col2}</PTableCell>
                {item.col3 !== undefined && <PTableCell>{item.col3}</PTableCell>}
                {item.col4 !== undefined && <PTableCell>{item.col4}</PTableCell>}
              </PTableRow>
            ))}
        </PTableBody>
      </PTable>
      {!loading && data.length === 0 && (
        <div className={styles.noData}>
          <PText align='center' className={styles.noDataText}>
            Keine Daten
          </PText>
        </div>
      )}
      {loading && (
        <div className={styles.loaderBlocker}>
          <PText align='center' className={styles.loaderText}>
            Daten werden geladen...
          </PText>
          <PSpinner className={styles.loaderSpinner} />
        </div>
      )}
    </>
  )
}

export default React.memo(TableWithAction)
